/**
 * Display formatting for KPI numbers.
 *
 * Formatting only — nothing here changes a value. Percentages arrive as
 * fractions (0.945 → "94.5%") because that is how the fact table stores them;
 * money is whole dollars in the grid (the cents are noise at portfolio scale)
 * and full precision in the drill-in, where the user is checking a number
 * against its source.
 */

import { type KpiUnit, isSupportedPeriodGrain } from "../../types/kpi";

/*
 * `currencySign: "accounting"` renders a negative as ($84,300) rather than
 * -$84,300. That is the convention every reader of a financial statement is
 * trained on, and it survives a photocopy, a projector and a scan of a printed
 * page in a way a leading hyphen does not.
 *
 * Applied to money only. A negative percentage or ratio is conventionally
 * written with a minus, so wrapping those in parentheses would be a house
 * style nobody else uses.
 */
const USD = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	currencySign: "accounting",
	maximumFractionDigits: 0,
});

const USD_EXACT = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	currencySign: "accounting",
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

const COUNT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

export function formatKpiValue(
	value: number | null | undefined,
	unit: KpiUnit,
): string {
	if (value === null || value === undefined || Number.isNaN(value)) return "—";
	switch (unit) {
		case "usd":
			return USD.format(value);
		case "pct":
			return `${(value * 100).toFixed(1)}%`;
		case "ratio":
			return value.toFixed(2);
		case "count":
		default:
			return COUNT.format(value);
	}
}

/** Full-precision variant for the drill-in panel. */
export function formatKpiValueExact(
	value: number | null | undefined,
	unit: KpiUnit,
): string {
	if (value === null || value === undefined || Number.isNaN(value)) return "—";
	switch (unit) {
		case "usd":
			return USD_EXACT.format(value);
		case "pct":
			return `${(value * 100).toFixed(2)}%`;
		case "ratio":
			return value.toFixed(4);
		case "count":
		default:
			return COUNT.format(value);
	}
}

/**
 * Column header for a period date (`YYYY-MM-DD`) → "Jan 2026".
 *
 * Parsed by hand rather than via `new Date(iso)`: that constructor reads a
 * bare date as UTC midnight and then renders it in local time, which moves the
 * month backwards for anyone west of Greenwich — every US user (see repo memory
 * on user geography).
 */
const MONTHS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

/**
 * Column header for a period date, in the terms of the grain it belongs to.
 *
 * Aggregate period keys are the FIRST DAY of the bucket (backend contract), so
 * the same `YYYY-MM-DD` string means "October 2023", "Q4 2023" or "2023"
 * depending only on `table.period_grain`. Labelling a quarterly column "Oct
 * 2023" would name one third of the number it sits above, which is why the
 * grain travels with the periods rather than being guessed from the dates.
 *
 * A grain this build cannot label — a newer server's bucket — returns the raw
 * period key. The date is then unglamorous but TRUE; "Oct 2023" over a weekly
 * or trailing-twelve bucket would name a period the number does not cover, and
 * a reader has no way to tell that label from a real one.
 */
export function formatPeriod(
	periodDate: string,
	grain: string = "month",
): string {
	if (!isSupportedPeriodGrain(grain)) return periodDate;
	const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(periodDate);
	if (!match) return periodDate;
	const year = match[1];
	if (grain === "year") return year;
	const monthIndex = Number(match[2]) - 1;
	if (monthIndex < 0 || monthIndex > 11) return periodDate;
	if (grain === "quarter") return `Q${Math.floor(monthIndex / 3) + 1} ${year}`;
	return `${MONTHS[monthIndex]} ${year}`;
}

const PLAIN = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

/**
 * A number with NO unit applied.
 *
 * For values whose unit is not known — a pre-v1.4 server sends inputs without
 * one. Borrowing the surrounding cell's unit instead is how `total_units` under
 * a vacancy rate came out as "10,000.00%": a plausible-looking figure that is
 * wrong by two orders of magnitude and a unit. An undressed number is the
 * honest rendering of "we were not told what this is measured in".
 */
export function formatUnitlessValue(
	value: number | null | undefined,
): string {
	if (value === null || value === undefined || Number.isNaN(value)) return "—";
	return PLAIN.format(value);
}

/** Extraction timestamp (epoch ms) for the drill-in's provenance block. */
export function formatExtractedAt(epochMs: number | null | undefined): string {
	if (epochMs === null || epochMs === undefined) return "—";
	return new Date(epochMs).toLocaleString("en-US", {
		dateStyle: "medium",
		timeStyle: "short",
	});
}
